import { useEffect, useMemo, useState } from "react";
import { AttentionArena } from "./components/AttentionArena";
import { ConsentGate } from "./components/ConsentGate";
import { HelpPage } from "./components/HelpPage";
import { SettingsPage } from "./components/SettingsPage";
import { StatsPage } from "./components/StatsPage";
import { useAppStore } from "./stores/useAppStore";
import { getDeviceHash } from "./lib/device";
import { AppRoute } from "./types";
import "./styles.css";

const routes: { path: AppRoute; label: string }[] = [
  { path: "/", label: "Play" },
  { path: "/stats", label: "Commons" },
  { path: "/settings", label: "Settings" },
  { path: "/help", label: "Help" }
];

const LINKEDIN_URL = "https://www.linkedin.com/in/mindful-abhay/";
const COFFEE_URL = "https://buymeacoffee.com/abhaypratapsingh";

function currentRoute(): AppRoute {
  const path = window.location.pathname as AppRoute;
  return routes.some((route) => route.path === path) ? path : "/";
}

export default function App() {
  const [route, setRoute] = useState<AppRoute>(() => currentRoute());
  const { consented, lastError, setDeviceHash } = useAppStore();
  const subtitle = useMemo(() => {
    if (route === "/stats") return "The public heatmap breathing back.";
    if (route === "/settings") return "Your local archive, your consent, your device key.";
    if (route === "/help") return "Cognitive phenomenon, mechanics, and privacy line.";
    return "Infer attention from touch and dwell; keep the glow.";
  }, [route]);

  useEffect(() => {
    getDeviceHash().then(setDeviceHash).catch(() => undefined);
    const onPop = () => setRoute(currentRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [setDeviceHash]);

  const navigate = (path: AppRoute) => {
    history.pushState(null, "", path);
    setRoute(path);
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" onClick={(event) => { event.preventDefault(); navigate("/"); }}>
          <span className="brand-mark">◉</span>
          <span>Afterglow Atlas</span>
        </a>
        <div className="topbar-actions">
          <nav aria-label="Primary">
            {routes.map((item) => (
              <a
                key={item.path}
                href={item.path}
                aria-current={route === item.path ? "page" : undefined}
                onClick={(event) => { event.preventDefault(); navigate(item.path); }}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="creator-actions" aria-label="Creator links">
            <a className="creator-link" href={LINKEDIN_URL} target="_blank" rel="noreferrer">LinkedIn</a>
            <a className="creator-link support" href={COFFEE_URL} target="_blank" rel="noreferrer">Buy me a coffee</a>
          </div>
        </div>
      </header>

      <main>
        <div className="masthead">
          <p>{subtitle}</p>
          <span>{consented ? "human-attested" : "awaiting consent"}</span>
        </div>
        {!consented && <ConsentGate />}
        {lastError && <div className="toast error" role="alert">{lastError}</div>}
        {consented && route === "/" && <AttentionArena />}
        {consented && route === "/stats" && <StatsPage />}
        {consented && route === "/settings" && <SettingsPage />}
        {route === "/help" && <HelpPage />}
        <footer className="creator-footer">
          Built by <a href={LINKEDIN_URL} target="_blank" rel="noreferrer">Mindful Abhay</a>. Support future cognition tools at <a href={COFFEE_URL} target="_blank" rel="noreferrer">Buy Me a Coffee</a>.
        </footer>
      </main>
    </div>
  );
}
